import React, { useEffect, useRef, useState } from 'react';
import { BOAPI } from "../service/UsersService";

const fileToBase64 = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => resolve(reader.result);
  reader.onerror = reject;
  reader.readAsDataURL(file);
});

const emptyTarget = () => ({
  name: '',
  description: '',
  associationNumber: '',
  contactName: '',
  contactPhone: '',
  contactEmail: '',
  hasSection46: false,
  imgUrl: '',
  displayOrder: 0,
  isActive: true,
});

const validate = (data) => {
  if (!data.name?.trim()) return 'נא להזין שם קופה';
  return null;
};

// שדה תמונה עם העלאה מהמחשב או קישור ישיר — אותו דפוס כמו סטוריז/פרסומות
const ImageField = ({ value, onChange }) => {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setUploading(true);
    try {
      const base64 = await fileToBase64(file);
      const ext = file.name.includes('.') ? file.name.split('.').pop() : '';
      const resp = await BOAPI.uploadDonationTargetImage(base64, ext);
      if (resp?.code === 0 && resp.url) onChange(resp.url);
      else window.alert(resp?.msg || 'העלאת התמונה נכשלה');
    } catch {
      window.alert('העלאת התמונה נכשלה');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div style={s.formGroup}>
      <label style={s.label}>תמונה / לוגו (אופציונלי)</label>
      <div style={s.uploadRow}>
        <input
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          style={{ ...s.input, flex: 1, minWidth: 0, direction: 'ltr' }}
          placeholder="קישור לתמונה, או בחירת קובץ מהמכשיר"
        />
        <button type="button" onClick={() => fileRef.current?.click()} style={s.uploadButton} disabled={uploading}>
          {uploading ? '⏳ מעלה...' : '📁 בחר תמונה'}
        </button>
        <input ref={fileRef} type="file" accept="image/*" onChange={handleFile} style={{ display: 'none' }} />
      </div>
      {value && <img src={value} alt="תצוגה מקדימה" style={s.preview} />}
    </div>
  );
};

// שדות הטופס המשותפים להוספה ולעריכה
const TargetFields = ({ data, onChange }) => (
  <>
    <div style={s.formRow}>
      <div style={s.formGroup}>
        <label style={s.label}>שם הקופה *</label>
        <input type="text" value={data.name} onChange={(e) => onChange('name', e.target.value)} style={s.input} maxLength={100} placeholder='לדוגמה: דוכני תפילין בשוק הכרמל' />
      </div>
      <div style={s.formGroup}>
        <label style={s.label}>סדר תצוגה</label>
        <input type="number" value={data.displayOrder} onChange={(e) => onChange('displayOrder', e.target.value)} style={s.input} />
      </div>
    </div>

    <div style={s.formGroup}>
      <label style={s.label}>תיאור (מוצג למשתמש בבחירת הקופה)</label>
      <textarea value={data.description} onChange={(e) => onChange('description', e.target.value)} style={s.textarea} rows={2} maxLength={500} />
    </div>

    <ImageField value={data.imgUrl} onChange={(v) => onChange('imgUrl', v)} />

    <div style={s.formRow}>
      <div style={s.formGroup}>
        <label style={s.label}>מספר עמותה (ע"ר)</label>
        <input type="text" value={data.associationNumber} onChange={(e) => onChange('associationNumber', e.target.value)} style={s.input} maxLength={50} />
      </div>
      <div style={s.formGroup}>
        <label style={s.checkboxLabel}>
          <input type="checkbox" checked={data.hasSection46} onChange={(e) => onChange('hasSection46', e.target.checked)} style={s.checkbox} />
          מוכרת לפי סעיף 46 (זיכוי מס)
        </label>
      </div>
    </div>

    <div style={s.formRow}>
      <div style={s.formGroup}>
        <label style={s.label}>איש קשר</label>
        <input type="text" value={data.contactName} onChange={(e) => onChange('contactName', e.target.value)} style={s.input} maxLength={100} />
      </div>
      <div style={s.formGroup}>
        <label style={s.label}>טלפון</label>
        <input type="text" value={data.contactPhone} onChange={(e) => onChange('contactPhone', e.target.value)} style={{ ...s.input, direction: 'ltr' }} maxLength={20} />
      </div>
      <div style={s.formGroup}>
        <label style={s.label}>אימייל</label>
        <input type="text" value={data.contactEmail} onChange={(e) => onChange('contactEmail', e.target.value)} style={{ ...s.input, direction: 'ltr' }} maxLength={150} />
      </div>
    </div>
  </>
);

// ניהול קופות הצדקה שמופיעות באפליקציה תחת "לאילו קופות לתרום?" (הגדרות קופת הצדקה).
// כרגע יש בפועל קופה פעילה אחת, אבל המנגנון תומך בכמה קופות — כל קופה שתתווסף כאן תופיע
// אוטומטית באפליקציה, בלי שינוי קוד. המשתמש בוחר עד 5 קופות והתרומה מתחלקת ביניהן.
const DonationTargetsTab = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [showAdd, setShowAdd] = useState(false);
  const [newItem, setNewItem] = useState(emptyTarget());
  const [editingId, setEditingId] = useState(null);
  const [editData, setEditData] = useState(null);

  useEffect(() => { load(); }, []);

  const load = async () => {
    try {
      setLoading(true);
      setError(null);
      const result = await BOAPI.fetchDonationTargets();
      if (Array.isArray(result) && result[0]?.[0]?.code === 0 && Array.isArray(result[1])) {
        setItems(result[1]);
      } else {
        setError(result?.[0]?.[0]?.msg || 'שגיאה בטעינת הנתונים — ודאו שהרצתם את DonationTargetsBO.sql');
      }
    } catch (err) {
      setError(err.message || 'שגיאה בטעינת הנתונים');
    } finally {
      setLoading(false);
    }
  };

  const run = async (fn) => {
    try {
      const resp = await fn();
      const status = Array.isArray(resp) ? resp[0]?.[0] : null;
      if (status?.code === 0) { setError(null); return true; }
      setError(status?.msg || 'הפעולה נכשלה');
    } catch (err) {
      setError(err.message || 'הפעולה נכשלה');
    }
    return false;
  };

  const handleAdd = async () => {
    const err = validate(newItem);
    if (err) { setError(err); return; }
    if (await run(() => BOAPI.addDonationTarget({ ...newItem, displayOrder: Number(newItem.displayOrder) || 0 }))) {
      setShowAdd(false);
      setNewItem(emptyTarget());
      load();
    }
  };

  const handleEdit = (item) => {
    setEditingId(item.id);
    setEditData({
      name: item.name || '',
      description: item.description || '',
      associationNumber: item.associationNumber || '',
      contactName: item.contactName || '',
      contactPhone: item.contactPhone || '',
      contactEmail: item.contactEmail || '',
      hasSection46: !!item.hasSection46,
      imgUrl: item.imgUrl || '',
      displayOrder: item.displayOrder ?? 0,
      isActive: !!item.isActive,
    });
  };

  const handleSaveEdit = async (id) => {
    const err = validate(editData);
    if (err) { setError(err); return; }
    if (await run(() => BOAPI.updateDonationTarget({ id, ...editData, displayOrder: Number(editData.displayOrder) || 0 }))) {
      setEditingId(null);
      setEditData(null);
      load();
    }
  };

  const handleDelete = async (item) => {
    if (!window.confirm(`למחוק את הקופה "${item.name}"?`)) return;
    if (await run(() => BOAPI.deleteDonationTarget(item.id))) load();
  };

  if (loading) return <div style={s.center}>טוען קופות...</div>;

  return (
    <div style={s.container}>
      <div style={s.headerSection}>
        <h2 style={s.title}>קופות צדקה</h2>
        {!showAdd && <button onClick={() => { setNewItem(emptyTarget()); setShowAdd(true); }} style={s.addButton}>➕ קופה חדשה</button>}
      </div>
      <div style={s.hint}>
        אלו הקופות שמוצגות למשתמשים בהגדרות קופת הצדקה שלהם ("לאילו קופות לתרום?"). כל משתמש בוחר עד 5 קופות פעילות, והתרומה שלו מתחלקת ביניהן שווה בשווה.
        קופה לא פעילה לא מוצגת למשתמשים חדשים, אבל נשארת אצל מי שכבר בחר בה.
      </div>

      {error && (
        <div style={s.error}>
          ❌ {error}
          <button onClick={() => setError(null)} style={s.closeErrorButton}>✕</button>
        </div>
      )}

      {showAdd && (
        <div style={s.formCard}>
          <h3 style={s.formTitle}>קופה חדשה</h3>
          <TargetFields data={newItem} onChange={(f, v) => setNewItem((p) => ({ ...p, [f]: v }))} />
          <div style={s.formButtons}>
            <button onClick={handleAdd} style={s.saveButton}>➕ הוסף</button>
            <button onClick={() => setShowAdd(false)} style={s.cancelButton}>✕ בטל</button>
          </div>
        </div>
      )}

      {items.length === 0 ? (
        <div style={s.noData}>אין קופות להצגה</div>
      ) : (
        <div style={s.tableWrapper}>
          <table style={s.table}>
            <thead>
              <tr style={s.headerRow}>
                <th style={s.th}></th>
                <th style={s.th}>שם</th>
                <th style={s.th}>ע"ר</th>
                <th style={s.th}>סדר</th>
                <th style={s.th}>סטטוס</th>
                <th style={s.th}>פעולות</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item) => (
                <React.Fragment key={item.id}>
                  <tr style={s.row}>
                    <td style={s.td}>{item.imgUrl ? <img src={item.imgUrl} alt="" style={s.thumb} /> : null}</td>
                    <td style={s.td}>{item.name}</td>
                    <td style={s.td}>{item.associationNumber || '—'}</td>
                    <td style={s.td}>{item.displayOrder}</td>
                    <td style={s.td}>
                      <span style={{ ...s.statusBadge, backgroundColor: item.isActive ? '#e8f5e9' : '#ffebee', color: item.isActive ? '#2e7d32' : '#c62828' }}>
                        {item.isActive ? '✓ פעיל' : 'לא פעיל'}
                      </span>
                    </td>
                    <td style={s.td}>
                      <div style={s.actionButtons}>
                        <button onClick={() => (editingId === item.id ? (setEditingId(null), setEditData(null)) : handleEdit(item))} style={s.editButton}>
                          {editingId === item.id ? '✕ בטל' : '✏️ ערוך'}
                        </button>
                        <button onClick={() => handleDelete(item)} style={s.cancelButton}>🗑️ מחק</button>
                      </div>
                    </td>
                  </tr>
                  {editingId === item.id && editData && (
                    <tr>
                      <td style={s.td} colSpan={6}>
                        <div style={s.formCard}>
                          <TargetFields data={editData} onChange={(f, v) => setEditData((p) => ({ ...p, [f]: v }))} />
                          <div style={s.formGroup}>
                            <label style={s.checkboxLabel}>
                              <input type="checkbox" checked={editData.isActive} onChange={(e) => setEditData((p) => ({ ...p, isActive: e.target.checked }))} style={s.checkbox} />
                              פעיל (מוצג למשתמשים)
                            </label>
                          </div>
                          <div style={s.formButtons}>
                            <button onClick={() => handleSaveEdit(item.id)} style={s.saveButton}>💾 שמור</button>
                            <button onClick={() => { setEditingId(null); setEditData(null); }} style={s.cancelButton}>✕ בטל</button>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const s = {
  container: { padding: '20px', direction: 'rtl', fontFamily: 'Arial, sans-serif' },
  headerSection: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px', marginBottom: '8px' },
  title: { margin: 0, color: '#1a1f36', fontSize: '24px', fontWeight: 'bold' },
  hint: { color: '#777', fontSize: '13px', marginBottom: '18px', lineHeight: 1.6 },
  addButton: { padding: '10px 20px', backgroundColor: '#4caf50', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '14px', fontWeight: 'bold' },

  formCard: { backgroundColor: '#f9f9f9', padding: '20px', borderRadius: '8px', border: '1px solid #ddd', marginBottom: '20px' },
  formTitle: { marginTop: 0, marginBottom: '16px', color: '#333', fontSize: '18px' },
  formGroup: { marginBottom: '15px' },
  formRow: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '15px' },
  label: { display: 'block', marginBottom: '5px', color: '#333', fontWeight: 'bold', fontSize: '14px' },
  input: { width: '100%', padding: '10px', fontSize: '14px', border: '1px solid #ddd', borderRadius: '4px', boxSizing: 'border-box', backgroundColor: 'white' },
  textarea: { width: '100%', padding: '10px', fontSize: '14px', border: '1px solid #ddd', borderRadius: '4px', boxSizing: 'border-box', fontFamily: 'Arial, sans-serif', resize: 'vertical' },
  uploadRow: { display: 'flex', gap: '8px', alignItems: 'stretch' },
  uploadButton: { flex: '0 0 auto', padding: '10px 14px', backgroundColor: '#eef1f6', color: '#333', border: '1px solid #ddd', borderRadius: '4px', cursor: 'pointer', fontSize: '13px', whiteSpace: 'nowrap' },
  preview: { marginTop: '10px', maxWidth: '160px', maxHeight: '160px', borderRadius: '8px', border: '1px solid #ddd', objectFit: 'contain', backgroundColor: '#fff' },
  checkbox: { marginLeft: '8px', cursor: 'pointer' },
  checkboxLabel: { display: 'flex', alignItems: 'center', cursor: 'pointer', fontSize: '14px', marginTop: '22px' },
  formButtons: { display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '10px' },

  tableWrapper: { overflowX: 'auto', borderRadius: '8px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' },
  table: { width: '100%', borderCollapse: 'collapse', backgroundColor: 'white' },
  headerRow: { backgroundColor: '#f5f5f5', borderBottom: '2px solid #ddd' },
  th: { padding: '12px', textAlign: 'right', fontWeight: 'bold', color: '#333', fontSize: '14px', borderBottom: '2px solid #ddd' },
  row: { borderBottom: '1px solid #eee' },
  td: { padding: '12px', color: '#666', fontSize: '14px', textAlign: 'right', verticalAlign: 'middle' },
  thumb: { width: '36px', height: '36px', objectFit: 'cover', borderRadius: '6px', border: '1px solid #eee', backgroundColor: '#f3f3f3' },

  statusBadge: { display: 'inline-block', padding: '6px 12px', borderRadius: '12px', fontSize: '12px', fontWeight: 'bold' },
  actionButtons: { display: 'flex', gap: '8px', justifyContent: 'flex-end' },
  editButton: { padding: '8px 16px', backgroundColor: '#1f6feb', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '12px', fontWeight: 'bold' },
  saveButton: { padding: '8px 14px', backgroundColor: '#2e7d32', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '12px', fontWeight: 'bold' },
  cancelButton: { padding: '8px 14px', backgroundColor: '#c62828', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '12px', fontWeight: 'bold' },

  center: { textAlign: 'center', padding: '40px', color: '#999' },
  error: { padding: '15px', backgroundColor: '#ffebee', color: '#c62828', borderRadius: '4px', marginBottom: '20px', fontSize: '14px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  closeErrorButton: { backgroundColor: 'transparent', color: '#c62828', border: 'none', cursor: 'pointer', fontSize: '18px', fontWeight: 'bold' },
  noData: { padding: '40px', textAlign: 'center', color: '#999', backgroundColor: '#f5f5f5', borderRadius: '4px' },
};

export default DonationTargetsTab;
